import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { JournalsApi } from '../../../apis/JournalsApi/JournalsApi.ts';
import { IJournalInput } from '../Journal.ts';
import { useLowFrequencyRefreshQuery } from '../../../queryDefaults/baseQueries/useLowFrequencyRefreshQuery.tsx';

// Query keys
export const journalKeys = {
  all: ['journals'] as const,
  lists: () => [...journalKeys.all, 'list'] as const,
  list: (filters: string) => [...journalKeys.lists(), { filters }] as const,
  details: () => [...journalKeys.all, 'detail'] as const,
  detail: (id: number) => [...journalKeys.details(), id] as const,
  questionTypes: () => [...journalKeys.all, 'questionTypes'] as const,
};

// Queries
export const useJournals = () => {
  return useQuery({
    queryKey: journalKeys.lists(),
    queryFn: () => JournalsApi.getAll(),
  });
};

export const useFilteredJournals = (
  therapeuticIndicationId?: number,
  sexId?: number,
) => {
  return useQuery({
    queryKey: journalKeys.list(
      `therapeuticIndication:${therapeuticIndicationId || 'any'}_sex:${sexId || 'any'}`,
    ),
    queryFn: () =>
      JournalsApi.getFilteredForPatient(therapeuticIndicationId, sexId),
    enabled: true,
  });
};

export const useJournal = (id: number) => {
  return useLowFrequencyRefreshQuery({
    queryKey: journalKeys.detail(id),
    queryFn: () => JournalsApi.getById(id),
    enabled: !!id,
  });
};

export const useJournalQuestionTypes = () => {
  return useQuery({
    queryKey: journalKeys.questionTypes(),
    queryFn: () => JournalsApi.getJournalQuestionTypes(),
  });
};

// Mutations
export const useCreateJournal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (journal: IJournalInput) => JournalsApi.create(journal),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journalKeys.lists() });
    },
  });
};

export const useUpdateJournal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, journal }: { id: number; journal: IJournalInput }) =>
      JournalsApi.update(id, journal),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: journalKeys.lists() });
      queryClient.invalidateQueries({ queryKey: journalKeys.detail(id) });
    },
  });
};

export const useDeleteJournal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => JournalsApi.deleteJournal(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journalKeys.lists() });
    },
  });
};

// Category mutations
export const useAddCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      journalId,
      category,
    }: {
      journalId: number;
      category: any;
    }) => JournalsApi.addCategory(journalId, category),
    onSuccess: (_, { journalId }) => {
      queryClient.invalidateQueries({
        queryKey: journalKeys.detail(journalId),
      });
    },
  });
};

export const useUpdateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      categoryId,
      category,
    }: {
      categoryId: number;
      category: any;
    }) => JournalsApi.updateCategory(categoryId, category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journalKeys.lists() });
    },
  });
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (categoryId: number) => JournalsApi.deleteCategory(categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journalKeys.lists() });
    },
  });
};

export const useReorderCategories = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      journalId,
      categoryIds,
    }: {
      journalId: number;
      categoryIds: number[];
    }) => JournalsApi.reorderCategories(journalId, categoryIds),
    onSuccess: (_, { journalId }) => {
      queryClient.invalidateQueries({
        queryKey: journalKeys.detail(journalId),
      });
    },
  });
};

// Question mutations
export const useAddQuestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      categoryId,
      question,
    }: {
      categoryId: number;
      question: any;
    }) => JournalsApi.addQuestion(categoryId, question),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journalKeys.lists() });
    },
  });
};

export const useUpdateQuestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      questionId,
      question,
    }: {
      questionId: number;
      question: any;
    }) => JournalsApi.updateQuestion(questionId, question),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journalKeys.lists() });
    },
  });
};

export const useDeleteQuestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (questionId: number) => JournalsApi.deleteQuestion(questionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journalKeys.lists() });
    },
  });
};

export const useReorderQuestions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      categoryId,
      questionIds,
    }: {
      categoryId: number;
      questionIds: number[];
    }) => JournalsApi.reorderQuestions(categoryId, questionIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: journalKeys.lists() });
    },
  });
};
